import getFooter from 'api/Footer/getFooter';
import getMenu from 'api/Header/getMenu';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import type { GetStaticProps, NextPage } from 'next';
import CompanyDirectory, { type CompanyDirectoryProps } from 'pages/CompanyDirectory';
import setCompanyDirPageAdConfig from 'services/Ad/config/setCompanyDirPageAdConfig';
import { setMockPianoConfig } from 'services/Piano';
import getCompanies from 'api/Companies/getCompanies';
import { layoutCount } from 'constants/constants';
import { setHeadData } from 'utils/metaDataUtils/headData';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';

const CompanyDirectoryPage: NextPage<CompanyDirectoryProps> = ({
  companies,
  mostPopular,
  title,
  description,
  pageSize,
}: CompanyDirectoryProps) => (
  <CompanyDirectory
    companies={companies}
    mostPopular={mostPopular}
    infiniteScroll={{
      numCompaniesPerPage: layoutCount.companyDirectory,
      slug: 'companies',
      taxonomy: 'directory',
    }}
    title={title}
    description={description}
    pageSize={pageSize}
    pageNum='1'
  />
);

export const getStaticProps: GetStaticProps = async () => {
  const pageSize = layoutCount.companyDirectory;
  try {
    const companies = await getCompanies(layoutCount.companyDirectory, 1, 'companies');
    const headerNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const headData = safe(() => setHeadData(companies.metaData), fallbackData.headData);
    const pageAdConfig = safe(() => setCompanyDirPageAdConfig(), fallbackData.pageAdConfig);
    const heroTitle = 'Fortune Company List';
    const heroDescription =
      'Welcome to the Fortune company index, a resource for discovering businesses across various industries and sectors. Fortune has highlighted each of these companies on one or more current or past <a href="https://fortune.com/ranking/" target="_blank">lists</a>: <a href="https://fortune.com/ranking/fortune500/" target="_blank">Fortune 500</a>, <a href="https://fortune.com/ranking/global500/" target="_blank">Global 500</a>, <a href="https://fortune.com/ranking/best-companies/" target="_blank">100 Best Companies to Work For</a>, and more. Whether you\'re a potential customer, an investor, or are simply curious about the business landscape, our directory offers valuable insights into a diverse array of companies.';
    const mostPopular = [
      {
        href: '/company/walmart/',
        label: 'Walmart',
      },
      {
        href: '/company/amazon-com/',
        label: 'Amazon',
      },
      {
        href: '/company/accenture/',
        label: 'Accenture',
      },
    ];

    return {
      props: {
        companies,
        description: heroDescription,
        footer,
        headData,
        headerNav,
        mostPopular,
        pageAdConfig,
        pageSize,
        pianoPageConfig: setMockPianoConfig(),
        platformCD: 'own',
        title: heroTitle,
      },
      revalidate: 600,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export default CompanyDirectoryPage;
